import { useCallback, useEffect, useMemo, useState } from 'react';
import { MagnifyingGlass } from 'shared/icons';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { Id, Mono } from '../../components/ui/Mono';
import { useToast } from '../../components/ui/Toast';
import DepositRulesManager from '../../components/warehouse/DepositRulesManager';
import PrinterDiscoverModal from '../../components/warehouse/PrinterDiscoverModal';
import RegistryManager from '../../components/warehouse/RegistryManager';
import SettingsManager from '../../components/warehouse/SettingsManager';
import { useWarehouseLabels } from '../../components/warehouse/warehouseLabels';
import { TestNetworkPrinterViaQz } from '../../components/warehouse/zplPrinting';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';

// M2 acrescenta "Regras de depósito" (aba própria, fora do RegistryManager)
// entre Depósitos e Impressoras. r4: cada aba exige a permissão granular
// `wms:manage:{id}` (o backend EXPANDE `wms:manage` em todas — admin enxerga
// tudo; papéis parciais só as abas deles).
// Os atributos de produto do WMS (is_bulky/track_supplier_lot) migraram para a
// página /produtos (gestão unificada do catálogo) — não são mais uma aba aqui.
const TAB_IDS = [
    'operators',
    'equipment',
    'lpn_prefixes',
    'service_order_types',
    'deposits',
    'deposit_rules',
    'printers',
    'settings',
];

// Porta padrão de impressoras de etiqueta (exibição quando a linha não tem
// porta gravada — o backend usa o mesmo default no envio).
const DEFAULT_PRINTER_PORT = 9100;

// Chaves preferidas do envelope de listagem, POR REGISTRO, em constante de
// módulo: identidade estável entre renders (não refaz o GET quando o
// dicionário remoto de rótulos chega e as configs são reconstruídas).
const REGISTRY_LIST_KEYS = {
    operators: ['operators'],
    equipment: ['equipment'],
    lpn_prefixes: ['lpn_prefixes', 'prefixes'],
    service_order_types: ['service_order_types', 'types'],
    deposits: ['deposits'],
    printers: ['printers'],
};

function renderFlagCell(value) {
    return value ? <Badge variant="success">Sim</Badge> : <span className="text-mono-400">—</span>;
}

// Configs das 6 abas de registro (a 7ª, Parâmetros, é o SettingsManager).
// CODEs e códigos são dados de máscara fixa → monoespaçados (ui/Mono).
// `employeeOptions` alimenta o select de funcionário da aba Operadores —
// pessoas são cadastradas em /funcionarios (entidade de sistema); aqui só se
// cria o VÍNCULO (funcionário + Função no WMS). `printerActions` traz os
// ganchos da aba Impressoras (teste por linha + botão de descoberta) — vivem
// na página porque precisam de toast/estado.
function buildRegistryConfigs(label, employeeOptions, printerActions) {
    return {
        operators: {
            key: 'operators',
            entityLabel: 'Operador',
            entityLabelPlural: label('wms.operators.title'),
            countLabel: 'operadores',
            description: label('wms.operators.description'),
            exportFileName: 'wms-operadores',
            list: api.getWarehouseOperators,
            create: api.createWarehouseOperator,
            update: api.updateWarehouseOperator,
            listKeys: REGISTRY_LIST_KEYS.operators,
            conflictField: 'employee_id',
            columns: [
                {
                    key: 'employee_name',
                    label: label('wms.field.employee'),
                    getValue: (row) => row.employee_name,
                    render: (row) => (
                        <span className="font-medium text-black dark:text-white">
                            {row.employee_name}
                            {row.employee_active ? null : (
                                <Badge variant="warning" className="ml-2">
                                    Funcionário inativo
                                </Badge>
                            )}
                        </span>
                    ),
                },
                {
                    key: 'employee_code',
                    label: label('wms.field.employee_code'),
                    getValue: (row) => row.employee_code,
                    render: (row) => <Id value={row.employee_code} />,
                },
                {
                    key: 'roles',
                    // Papéis do crachá (25/08): o que a pessoa pode fazer no chão.
                    label: label('wms.operators.roles'),
                    getValue: (row) => (row.roles || []).join(', '),
                    render: (row) =>
                        (row.roles || []).length ? (
                            <span className="flex flex-wrap gap-1">
                                {row.roles.map((r) => (
                                    <Badge key={r} variant={r === 'supervisor' ? 'info' : 'default'}>
                                        {label(`wms.operator_role.${r}`)}
                                    </Badge>
                                ))}
                            </span>
                        ) : (
                            <span className="text-mono-400">sem papel</span>
                        ),
                },
            ],
            fields: [
                {
                    name: 'employee_id',
                    label: label('wms.field.employee'),
                    type: 'select',
                    required: true,
                    options: employeeOptions,
                    help: 'Pessoas são cadastradas em Funcionários (menu do sistema); aqui você só vincula uma delas ao WMS.',
                    // O vínculo é DA pessoa: na edição troca-se a Função ou o
                    // status, nunca o funcionário (desative e crie outro).
                    visibleWhen: (_form, context) => !context?.isEditing,
                },
                {
                    name: 'roles',
                    label: label('wms.operators.roles'),
                    type: 'multicheck',
                    defaultValue: [],
                    // Supervisor é o master: ao marcá-lo a UI trava todas as demais
                    // marcadas — a leitura de "faz tudo". O backend deriva o resto.
                    masterOption: 'supervisor',
                    masterHint: 'Supervisor faz tudo — as demais funções ficam marcadas e travadas.',
                    help: 'O que este crachá pode fazer no chão. Pode ter várias (ex.: picking + packing para cobrir férias). Supervisor faz tudo.',
                    options: [
                        { value: 'picking', label: label('wms.operator_role.picking') },
                        { value: 'packing', label: label('wms.operator_role.packing') },
                        { value: 'fabrica', label: label('wms.operator_role.fabrica') },
                        { value: 'supervisor', label: label('wms.operator_role.supervisor') },
                    ],
                },
            ],
        },
        equipment: {
            key: 'equipment',
            entityLabel: 'Equipamento',
            entityLabelPlural: label('wms.equipment.title'),
            countLabel: 'equipamentos',
            description: label('wms.equipment.description'),
            exportFileName: 'wms-equipamentos',
            list: api.getWarehouseEquipment,
            create: api.createWarehouseEquipment,
            update: api.updateWarehouseEquipment,
            listKeys: REGISTRY_LIST_KEYS.equipment,
            conflictField: 'code',
            columns: [
                {
                    key: 'code',
                    label: label('wms.field.code'),
                    getValue: (row) => row.code,
                    render: (row) => <Id value={row.code} className="font-medium" />,
                },
                {
                    key: 'type',
                    label: label('wms.field.type'),
                    getValue: (row) => row.type,
                    render: (row) => <Badge>{label(`wms.equipment_type.${row.type}`)}</Badge>,
                },
            ],
            fields: [
                {
                    name: 'code',
                    label: label('wms.field.code'),
                    type: 'text',
                    required: true,
                    mono: true,
                    uppercase: true,
                    placeholder: 'ex.: FORK-01',
                },
                {
                    name: 'type',
                    label: label('wms.field.type'),
                    type: 'select',
                    defaultValue: 'FORKLIFT',
                    options: [
                        { value: 'FORKLIFT', label: label('wms.equipment_type.FORKLIFT') },
                        { value: 'PALLET_JACK', label: label('wms.equipment_type.PALLET_JACK') },
                    ],
                },
            ],
        },
        lpn_prefixes: {
            key: 'lpn_prefixes',
            entityLabel: 'Prefixo de LPN',
            entityLabelPlural: label('wms.lpn_prefixes.title'),
            countLabel: 'prefixos',
            description: label('wms.lpn_prefixes.description'),
            exportFileName: 'wms-prefixos-lpn',
            list: api.getWarehouseLpnPrefixes,
            create: api.createWarehouseLpnPrefix,
            update: api.updateWarehouseLpnPrefix,
            listKeys: REGISTRY_LIST_KEYS.lpn_prefixes,
            conflictField: 'code',
            columns: [
                {
                    key: 'code',
                    label: label('wms.field.code'),
                    getValue: (row) => row.code,
                    render: (row) => <Id value={row.code} className="font-medium" />,
                },
                {
                    key: 'label',
                    label: label('wms.field.label'),
                    getValue: (row) => row.label,
                },
            ],
            fields: [
                {
                    name: 'code',
                    label: label('wms.field.code'),
                    type: 'text',
                    required: true,
                    mono: true,
                    uppercase: true,
                    placeholder: 'ex.: MP',
                    maxLength: 10,
                },
                { name: 'label', label: label('wms.field.label'), type: 'text', required: true },
            ],
        },
        service_order_types: {
            key: 'service_order_types',
            entityLabel: 'Tipo de OS',
            entityLabelPlural: label('wms.service_order_types.title'),
            countLabel: 'tipos de OS',
            description: label('wms.service_order_types.description'),
            exportFileName: 'wms-tipos-os',
            list: api.getWarehouseServiceOrderTypes,
            create: api.createWarehouseServiceOrderType,
            update: api.updateWarehouseServiceOrderType,
            listKeys: REGISTRY_LIST_KEYS.service_order_types,
            conflictField: 'code',
            columns: [
                {
                    key: 'code',
                    label: label('wms.field.code'),
                    getValue: (row) => row.code,
                    render: (row) => <Id value={row.code} className="font-medium" />,
                },
                {
                    key: 'label',
                    label: label('wms.field.label'),
                    getValue: (row) => row.label,
                },
            ],
            fields: [
                {
                    name: 'code',
                    label: label('wms.field.code'),
                    type: 'text',
                    required: true,
                    mono: true,
                    uppercase: true,
                    placeholder: 'ex.: LIFT',
                },
                // Feedback do M0: cadastro de tipo é só código + rótulo (como
                // prefixo). Tipo criado aqui nasce GENERIC no backend; os 9 tipos
                // seed carregam os comportamentos que o motor do M5 usa.
                { name: 'label', label: label('wms.field.label'), type: 'text', required: true },
            ],
        },
        deposits: {
            key: 'deposits',
            entityLabel: 'Depósito',
            entityLabelPlural: label('wms.deposits.title'),
            countLabel: 'depósitos',
            // M2: o seed cria os depósitos com a replicação Bling DESLIGADA —
            // ligar (e informar o ID do Bling) é ação do dono, aqui na UI.
            description: `${label('wms.deposits.description')} ${label('wms.deposits.bling_hint')}`,
            exportFileName: 'wms-depositos',
            list: api.getWarehouseDeposits,
            create: api.createWarehouseDeposit,
            update: api.updateWarehouseDeposit,
            listKeys: REGISTRY_LIST_KEYS.deposits,
            conflictField: 'code',
            columns: [
                {
                    key: 'code',
                    label: label('wms.field.code'),
                    getValue: (row) => row.code,
                    render: (row) => <Id value={row.code} className="font-medium" />,
                },
                {
                    key: 'label',
                    label: label('wms.field.label'),
                    getValue: (row) => row.label,
                },
                {
                    key: 'is_sellable',
                    label: label('wms.deposit.is_sellable'),
                    getValue: (row) => (row.is_sellable ? 1 : 0),
                    render: (row) => renderFlagCell(row.is_sellable),
                },
                {
                    key: 'is_consumable',
                    label: label('wms.deposit.is_consumable'),
                    getValue: (row) => (row.is_consumable ? 1 : 0),
                    render: (row) => renderFlagCell(row.is_consumable),
                },
                {
                    key: 'mirror_to_bling',
                    label: label('wms.deposit.mirror_to_bling'),
                    getValue: (row) => (row.mirror_to_bling ? 1 : 0),
                    // Replicação sem mostrar PARA ONDE seria meia informação: o
                    // ID do depósito no Bling aparece junto da flag.
                    render: (row) =>
                        row.mirror_to_bling ? (
                            <span className="inline-flex items-center gap-1.5">
                                {renderFlagCell(true)}
                                <Id value={row.bling_deposit_id} className="text-xs" />
                            </span>
                        ) : (
                            renderFlagCell(false)
                        ),
                },
            ],
            fields: [
                {
                    name: 'code',
                    label: label('wms.field.code'),
                    type: 'text',
                    required: true,
                    mono: true,
                    uppercase: true,
                    placeholder: 'ex.: DEP-01',
                },
                { name: 'label', label: label('wms.field.label'), type: 'text', required: true },
                {
                    name: 'is_sellable',
                    label: label('wms.deposit.is_sellable'),
                    type: 'checkbox',
                    help: label('wms.deposit.is_sellable_help'),
                },
                {
                    name: 'is_consumable',
                    label: label('wms.deposit.is_consumable'),
                    type: 'checkbox',
                    help: label('wms.deposit.is_consumable_help'),
                },
                {
                    name: 'mirror_to_bling',
                    label: label('wms.deposit.mirror_to_bling'),
                    type: 'checkbox',
                    help: label('wms.deposit.mirror_to_bling_help'),
                },
                {
                    name: 'bling_deposit_id',
                    label: label('wms.field.bling_deposit_id'),
                    type: 'text',
                    mono: true,
                    required: true,
                    placeholder: 'ex.: 14887334ies',
                    help: label('wms.deposit.bling_deposit_id_help'),
                    // Só existe quando a replicação está ligada — sem ela o campo
                    // não faz sentido (feedback do M0).
                    visibleWhen: (form) => Boolean(form.mirror_to_bling),
                },
            ],
        },
        printers: {
            key: 'printers',
            entityLabel: 'Impressora',
            entityLabelPlural: label('wms.printers.title'),
            countLabel: 'impressoras',
            description: label('wms.printers.description'),
            exportFileName: 'wms-impressoras',
            list: api.getWarehousePrinters,
            create: api.createWarehousePrinter,
            update: api.updateWarehousePrinter,
            listKeys: REGISTRY_LIST_KEYS.printers,
            conflictField: 'code',
            // "Detectar na rede" abre o mini-modal de descoberta (página).
            headerExtra: printerActions.headerExtra,
            columns: [
                {
                    key: 'code',
                    label: label('wms.field.code'),
                    getValue: (row) => row.code,
                    render: (row) => <Id value={row.code} className="font-medium" />,
                },
                {
                    key: 'label',
                    label: label('wms.field.label'),
                    getValue: (row) => row.label,
                },
                {
                    key: 'host_port',
                    label: label('wms.printer.field.host_port'),
                    getValue: (row) => `${row.host}:${row.port ?? DEFAULT_PRINTER_PORT}`,
                    render: (row) => <Mono>{`${row.host}:${row.port ?? DEFAULT_PRINTER_PORT}`}</Mono>,
                },
                {
                    key: 'test',
                    label: label('wms.printer.test'),
                    sortable: false,
                    searchable: false,
                    exportable: false,
                    getValue: () => '',
                    // Teste de vida: abre/fecha a porta TCP, não imprime.
                    render: (row) => (
                        <Button
                            variant="ghost"
                            size="sm"
                            loading={printerActions.testingId === row.id}
                            onClick={(event) => {
                                event.stopPropagation();
                                printerActions.onTest(row);
                            }}
                        >
                            {label('wms.printer.test')}
                        </Button>
                    ),
                },
            ],
            fields: [
                {
                    name: 'code',
                    label: label('wms.field.code'),
                    type: 'text',
                    required: true,
                    mono: true,
                    uppercase: true,
                    placeholder: 'ex.: ZEB-01',
                },
                { name: 'label', label: label('wms.field.label'), type: 'text', required: true },
                // Este cadastro é SÓ de impressora de REDE (decisão do dono):
                // se você está aqui, é para cadastrar algo que está na rede.
                // USB é pessoal do terminal e aparece SÓ na hora de imprimir
                // (grupo "USB desta máquina", via QZ Tray) — não se cadastra.
                {
                    name: 'host',
                    label: label('wms.printer.field.host'),
                    type: 'text',
                    required: true,
                    mono: true,
                    placeholder: 'ex.: 192.168.0.50',
                },
                {
                    name: 'port',
                    label: label('wms.printer.field.port'),
                    type: 'number',
                    mono: true,
                    min: 1,
                    max: 65535,
                    defaultValue: String(DEFAULT_PRINTER_PORT),
                    help: label('wms.printer.port_help'),
                },
            ],
        },
    };
}

/**
 * /warehouse — Configurações do Armazém (console do Supervisor, desktop).
 * Abas: Operadores, Equipamentos, Prefixos de LPN, Tipos de OS, Depósitos,
 * Regras de depósito (M2), Produtos (M2), Impressoras e Parâmetros. Abas de
 * registro: listagem + criar/editar em Modal + ativar/desativar (PATCH
 * active). SEM DELETE. Wire snake_case.
 */
const WarehouseConfigurationsPage = () => {
    const { label } = useWarehouseLabels();
    const { checkPermission } = useAuth();
    const toast = useToast();
    const [activeTab, setActiveTab] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    // Aba Impressoras: teste de vida por linha, mini-modal de descoberta e o
    // prefill do "+ cadastrar" (abre o form de criação já com host/porta).
    const [testingPrinterId, setTestingPrinterId] = useState(null);
    const [isDiscoverOpen, setDiscoverOpen] = useState(false);
    const [printerPrefill, setPrinterPrefill] = useState(null);

    // r4: só as abas PERMITIDAS aparecem (permissão granular por aba). A aba
    // ativa é derivada — se a escolhida não é (mais) permitida, cai na
    // primeira visível; sem nenhuma, a página vira o estado vazio explicativo.
    const visibleTabIds = TAB_IDS.filter((tabId) => checkPermission(`wms:manage:${tabId}`));
    const currentTab = visibleTabIds.includes(activeTab) ? activeTab : (visibleTabIds[0] ?? null);

    // Funcionários ATIVOS alimentam o select do vínculo. Recarrega ao entrar na
    // aba Operadores: o fluxo normal é criar a pessoa em /funcionarios e vir
    // direto para cá — a lista precisa refletir isso sem F5.
    const loadEmployeeOptions = useCallback(async () => {
        try {
            const data = await api.getEmployees({ active: 'true', limit: 1000 });
            const rows = Array.isArray(data?.results) ? data.results : [];
            setEmployeeOptions([
                { value: '', label: 'Selecione um funcionário…' },
                ...rows.map((employee) => ({
                    value: employee.id,
                    label: `${employee.name} — ${employee.code}`,
                })),
            ]);
        } catch {
            setEmployeeOptions([{ value: '', label: 'Erro ao carregar funcionários — recarregue a página' }]);
        }
    }, []);

    useEffect(() => {
        if (currentTab === 'operators') {
            loadEmployeeOptions();
        }
    }, [currentTab, loadEmployeeOptions]);

    // Teste de vida da impressora (abre/fecha a porta TCP — não imprime) pelo
    // QZ Tray DESTE computador: a impressora está na rede do armazém, que o
    // servidor na nuvem não alcança. QZ fora do ar vira erro com a instrução.
    const handleTestPrinter = useCallback(
        async (row) => {
            setTestingPrinterId(row.id);
            try {
                const ok = await TestNetworkPrinterViaQz(row, label);
                const target = `${row.host}:${row.port ?? DEFAULT_PRINTER_PORT}`;
                if (ok) {
                    toast.success(`${label('wms.printer.test_ok')} — ${target}`);
                } else {
                    toast.error(`${label('wms.printer.test_fail')} — ${target}`);
                }
            } catch (error) {
                toast.error(error);
            } finally {
                setTestingPrinterId(null);
            }
        },
        [toast, label],
    );

    const registryConfigs = useMemo(
        () =>
            buildRegistryConfigs(label, employeeOptions, {
                onTest: handleTestPrinter,
                testingId: testingPrinterId,
                headerExtra: (
                    <Button variant="secondary" onClick={() => setDiscoverOpen(true)} className="w-full sm:w-auto">
                        <MagnifyingGlass size={18} className="mr-2" />
                        {label('wms.printer.discover')}
                    </Button>
                ),
            }),
        [label, employeeOptions, handleTestPrinter, testingPrinterId],
    );

    const tabs = visibleTabIds.map((tabId) => ({ id: tabId, label: label(`wms.tab.${tabId}`) }));

    // Nenhuma aba permitida → estado vazio explicativo (a rota /warehouse
    // aceita QUALQUER granular; quem chega aqui sem nenhuma vê o porquê).
    if (visibleTabIds.length === 0) {
        return (
            <div className="space-y-6">
                <PageHeader title={label('wms.config.title')} subtitle={label('wms.config.subtitle')} />
                <div className="rounded-xl border border-mono-200 bg-white p-10 text-center dark:border-mono-800 dark:bg-mono-950">
                    <p className="text-sm font-medium text-black dark:text-white">
                        {label('wms.config.no_tabs_title')}
                    </p>
                    <p className="mx-auto mt-1 max-w-xl text-sm text-mono-600 dark:text-mono-400">
                        {label('wms.config.no_tabs_hint')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader title={label('wms.config.title')} subtitle={label('wms.config.subtitle')} />

            <div className="border-b border-mono-200 dark:border-mono-800">
                <nav className="flex space-x-8 overflow-x-auto">
                    {tabs.map((tab) => {
                        const isActive = tab.id === currentTab;
                        return (
                            <button
                                type="button"
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                aria-current={isActive ? 'true' : undefined}
                                className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                                    isActive
                                        ? 'border-black text-black dark:border-white dark:text-white'
                                        : 'border-transparent text-mono-500 hover:border-mono-300 hover:text-mono-700 dark:hover:text-mono-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {currentTab === 'settings' ? (
                <SettingsManager />
            ) : currentTab === 'deposit_rules' ? (
                <DepositRulesManager />
            ) : (
                // key remonta a seção ao trocar de aba: estado de modal/busca
                // não vaza de um cadastro para o outro.
                <RegistryManager
                    key={currentTab}
                    config={registryConfigs[currentTab]}
                    createPrefill={currentTab === 'printers' ? printerPrefill : null}
                    onCreatePrefillConsumed={() => setPrinterPrefill(null)}
                />
            )}

            <PrinterDiscoverModal
                isOpen={isDiscoverOpen}
                onClose={() => setDiscoverOpen(false)}
                onRegister={(found) => {
                    // Pré-preenche o form de criação (kind REDE + host/porta
                    // descobertos); código e rótulo ficam para o usuário.
                    setDiscoverOpen(false);
                    setPrinterPrefill({
                        kind: 'NETWORK',
                        host: found.host,
                        port: String(found.port ?? DEFAULT_PRINTER_PORT),
                    });
                }}
            />
        </div>
    );
};

export default WarehouseConfigurationsPage;
